import React, { useState, useRef, useEffect } from 'react'
import { Input, Icon, Popover, Empty } from 'antd'
import _ from 'lodash'
import './CommentInput.scss'

const MENTION_TYPES = {
  CHARACTER: 'character',
  LOCATION: 'location',
  OUTFIT: 'outfit',
  PROP: 'prop'
}

const CommentInput = ({
  rows = 4,
  label = 'Prompt',
  text = '',
  value: propValue,
  onChange,
  disabled = false,
  placeholder = 'Write a comment...',
  characterList = [],
  locationReference = {},
  selectedCharacter = [],
  locationMap = {},
  outfitMap = {},
  outfitsReference = {},
  propMap = {},
  propsReference = {}
}) => {
  const incomingValue = propValue !== undefined ? propValue : text
  const [value, setValue] = useState(incomingValue || '')
  const [mentionPopup, setMentionPopup] = useState({
    visible: false,
    type: null,
    query: '',
    cursorPos: 0
  })
  const [inputWidth, setInputWidth] = useState(0)

  const containerRef = useRef(null)
  const textareaRef = useRef(null)
  const backdropRef = useRef(null)
  const isUserTyping = useRef(false)
  const typingTimeout = useRef(null)

  const syncBackdropMetrics = () => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea
    const backdrop = backdropRef.current

    if (!textarea || !backdrop || typeof window === 'undefined') return

    const computedStyle = window.getComputedStyle(textarea)
    const mirroredProperties = [
      'boxSizing',
      'fontFamily',
      'fontSize',
      'fontStyle',
      'fontWeight',
      'letterSpacing',
      'lineHeight',
      'overflowWrap',
      'paddingTop',
      'paddingRight',
      'paddingBottom',
      'paddingLeft',
      'tabSize',
      'textAlign',
      'textIndent',
      'textTransform',
      'whiteSpace',
      'wordBreak',
      'wordSpacing'
    ]

    mirroredProperties.forEach(property => {
      backdrop.style[property] = computedStyle[property]
    })

    backdrop.style.borderTopWidth = computedStyle.borderTopWidth
    backdrop.style.borderRightWidth = computedStyle.borderRightWidth
    backdrop.style.borderBottomWidth = computedStyle.borderBottomWidth
    backdrop.style.borderLeftWidth = computedStyle.borderLeftWidth
  }

  useEffect(() => {
    if (!isUserTyping.current && incomingValue !== value) {
      setValue(incomingValue || '')
    }
  }, [incomingValue])

  useEffect(() => {
    syncBackdropMetrics()
  })

  useEffect(() => {
    const container = containerRef.current
    const textarea = textareaRef.current?.resizableTextArea?.textArea

    if (!container) return undefined

    const syncLayout = () => {
      setInputWidth(container.offsetWidth)
      syncBackdropMetrics()
    }

    syncLayout()
    window.addEventListener('resize', syncLayout)

    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncLayout)
      resizeObserver.observe(container)
      if (textarea) resizeObserver.observe(textarea)
    }

    return () => {
      window.removeEventListener('resize', syncLayout)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [])

  const handleScroll = e => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.target.scrollTop
      backdropRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  const detectMentions = (text, cursorPos) => {
    const textBeforeCursor = text.substring(0, cursorPos)
    const textAfterCursor = text.substring(cursorPos)

    const tagMatchBefore = textBeforeCursor.match(/(__[^_]*)$/)
    const tagMatchAfter = textAfterCursor.match(/^([^_]*__)/)

    if (tagMatchBefore && tagMatchAfter) {
      const tagContent = (tagMatchBefore[1] + tagMatchAfter[1]).replace(
        /^__|__$/g,
        ''
      )
      let type = MENTION_TYPES.CHARACTER
      if (tagContent.startsWith('location')) type = MENTION_TYPES.LOCATION
      if (tagContent.startsWith('outfit')) type = MENTION_TYPES.OUTFIT
      if (tagContent.startsWith('prop')) type = MENTION_TYPES.PROP
      setMentionPopup({ visible: true, type, query: tagContent, cursorPos })
      return true
    }

    const charMatch = textBeforeCursor.match(/@(\w*)$/)
    const locMatch = textBeforeCursor.match(/(?:^|[^\w_])[#_]([\d\.]*)$/)
    const outfitMatch = textBeforeCursor.match(/\[([\w\.]*)$/)
    const propMatch = textBeforeCursor.match(/!([\w\.]*)$/)

    if (charMatch)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.CHARACTER,
        query: charMatch[1],
        cursorPos
      })
    else if (locMatch)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.LOCATION,
        query: locMatch[1],
        cursorPos
      })
    else if (outfitMatch)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.OUTFIT,
        query: outfitMatch[1],
        cursorPos
      })
    else if (propMatch)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.PROP,
        query: propMatch[1],
        cursorPos
      })
    else setMentionPopup(prev => ({ ...prev, visible: false }))
  }

  const handleChange = e => {
    isUserTyping.current = true
    const newText = e.target.value
    setValue(newText)
    onChange && onChange(newText)
    detectMentions(newText, e.target.selectionStart)
    if (typingTimeout.current) clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false
    }, 600)
  }

  const handleCursorMove = e => {
    const navKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ]
    if (e.type === 'keyup' && navKeys.includes(e.key)) {
      setMentionPopup(prev => ({ ...prev, visible: false }))
      return
    }
    if (!isUserTyping.current) detectMentions(value, e.target.selectionStart)
  }

  const handleBlur = () => {
    setTimeout(() => {
      setMentionPopup(prev => ({ ...prev, visible: false }))
    }, 150)
  }

  const insertTag = (tag, type) => {
    const el = textareaRef.current.resizableTextArea.textArea
    const start = el.selectionStart
    const textBefore = value.substring(0, start)
    const textAfter = value.substring(start)
    let matchStart = -1,
      replaceLen = 0

    const tagMatchBefore = textBefore.match(/(__[^_]*)$/)
    const tagMatchAfter = textAfter.match(/^([^_]*__)/)

    if (tagMatchBefore && tagMatchAfter) {
      matchStart = textBefore.length - tagMatchBefore[1].length
      replaceLen = tagMatchBefore[1].length + tagMatchAfter[1].length
    } else {
      let queryMatch
      if (type === MENTION_TYPES.CHARACTER)
        queryMatch = textBefore.match(/@(\w*)$/)
      else if (type === MENTION_TYPES.LOCATION)
        queryMatch = textBefore.match(/(?:^|[^\w_])[#_]([\d\.]*)$/)
      else if (type === MENTION_TYPES.OUTFIT)
        queryMatch = textBefore.match(/\[([\w\.]*)$/)
      else if (type === MENTION_TYPES.PROP)
        queryMatch = textBefore.match(/!([\w\.]*)$/)

      if (queryMatch) {
        const triggerMatch = queryMatch[0].match(/[#@_\[!]/)
        matchStart =
          textBefore.lastIndexOf(queryMatch[0]) +
          queryMatch[0].indexOf(triggerMatch[0])
        replaceLen = textBefore.length - matchStart
      }
    }

    if (matchStart === -1) return

    // FIXED: Always ensuring technical key format (__character1__) is inserted
    let finalTag = tag.startsWith('__') ? tag : `__${tag}__`
    if (type === MENTION_TYPES.LOCATION && !tag.includes('location'))
      finalTag = `__location${tag}__`
    if (type === MENTION_TYPES.OUTFIT && !tag.includes('outfit'))
      finalTag = `__outfit${tag}__`
    if (type === MENTION_TYPES.PROP && !tag.includes('prop'))
      finalTag = `__prop${tag}__`

    const newVal =
      value.substring(0, matchStart) +
      finalTag +
      ' ' +
      value.substring(matchStart + replaceLen)
    setValue(newVal)
    onChange && onChange(newVal)
    setMentionPopup(prev => ({ ...prev, visible: false }))

    setTimeout(() => {
      el.focus()
      el.setSelectionRange(
        matchStart + finalTag.length + 1,
        matchStart + finalTag.length + 1
      )
    }, 0)
  }

  const getFilteredEntities = () => {
    const { type, query } = mentionPopup
    const q = query.toLowerCase()

    if (type === MENTION_TYPES.CHARACTER) {
      return (selectedCharacter || [])
        .filter(
          item =>
            item.original.toLowerCase().includes(q) ||
            item.current.toLowerCase().includes(q)
        )
        .map(item => {
          const cleanName = item.current.replace(/^__|__$/g, '')
          const details = characterList.find(c => c.name === cleanName)
          return {
            id: item.tag,
            name: item.original, // The technical key like __character3__
            label: cleanName, // The display name like Prabin
            image: details?.attributes?.profile_pic,
            icon: 'user'
          }
        })
    }

    const mapSource =
      type === MENTION_TYPES.LOCATION
        ? locationMap
        : type === MENTION_TYPES.OUTFIT
        ? outfitMap
        : propMap
    const refSource =
      type === MENTION_TYPES.LOCATION
        ? locationReference
        : type === MENTION_TYPES.OUTFIT
        ? outfitsReference
        : propsReference
    const icon =
      type === MENTION_TYPES.LOCATION
        ? 'environment'
        : type === MENTION_TYPES.OUTFIT
        ? 'skin'
        : 'tool'

    return Object.keys(mapSource || {})
      .filter(k => k.toLowerCase().includes(q))
      .map(k => ({
        id: k,
        name: k,
        label: k,
        description: mapSource[k],
        image: refSource[k]?.uri || refSource[k]?.url || refSource[k]?.image,
        icon
      }))
  }

  const renderBackdropContent = () => {
    if (!value) return null
    return value.split(/(__[\s\S]+?__)/g).map((part, i) => {
      if (part.match(/^__[\s\S]+?__$/)) {
        let isValid = false

        if (part.includes('__location'))
          isValid = !!(locationReference[part] || locationMap[part])
        else if (part.includes('__outfit'))
          isValid = !!(outfitsReference[part] || outfitMap[part])
        else if (part.includes('__prop'))
          isValid = !!(propsReference[part] || propMap[part])
        else {
          // FIXED: Validating specifically against the "original" technical tag
          isValid = (selectedCharacter || []).some(m => m.original === part)
        }

        return (
          <span
            key={i}
            className={`highlight-tag ${isValid ? 'valid' : 'invalid'}`}
          >
            {part}
          </span>
        )
      }
      return part
    })
  }

  const promptHelpContent = (
    <div className="comment-input-help">
      <div className="comment-input-help__title">Prompt references</div>
      <div className="comment-input-help__section">
        <div className="comment-input-help__row">
          <span className="comment-input-help__token">@</span>
          <span>Find and insert a selected character.</span>
        </div>
        <div className="comment-input-help__row">
          <span className="comment-input-help__token">#</span>
          <span>Find and insert a location reference.</span>
        </div>
        <div className="comment-input-help__row">
          <span className="comment-input-help__token">[</span>
          <span>Find and insert an outfit reference.</span>
        </div>
        <div className="comment-input-help__row">
          <span className="comment-input-help__token">!</span>
          <span>Find and insert a prop reference.</span>
        </div>
      </div>
      <div className="comment-input-help__note">
        Highlighted tags such as <code>__character1__</code> are linked to
        project context. Blue tags are resolved; red tags need a matching
        reference. Use Context Entities to change or fix them.
      </div>
    </div>
  )

  return (
    <div className="rich-comment-input" ref={containerRef}>
      <div
        className="comment-input-header"
        style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}
      >
        <label style={{ marginRight: 8, fontWeight: 'bold' }}>{label}</label>
        <Popover
          content={promptHelpContent}
          overlayClassName="comment-input-help-popover"
          placement="topLeft"
          trigger="click"
          getPopupContainer={() => containerRef.current || document.body}
        >
          <button
            type="button"
            className="comment-input-help-trigger"
            aria-label="Prompt reference help"
            title="Prompt reference help"
            onMouseDown={e => e.preventDefault()}
          >
            <Icon type="info" />
          </button>
        </Popover>
      </div>

      <Popover
        visible={mentionPopup.visible}
        overlayClassName="mention-popover-overlay"
        overlayStyle={{ width: inputWidth }}
        placement="bottomLeft"
        trigger="click"
        getPopupContainer={() => containerRef.current}
        content={
          <div
            className="mention-list"
            style={{ maxHeight: 250, overflowY: 'auto' }}
          >
            {getFilteredEntities().length > 0 ? (
              getFilteredEntities().map(item => (
                <div
                  key={item.id}
                  className="mention-item"
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => {
                    e.stopPropagation()
                    // This ensures __character3__ is inserted, not the name
                    insertTag(item.name, mentionPopup.type)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      style={{
                        width: 32,
                        height: 32,
                        marginRight: 10,
                        borderRadius: 4,
                        objectFit: 'cover'
                      }}
                      alt=""
                    />
                  ) : (
                    <Icon
                      type={item.icon}
                      style={{
                        fontSize: 20,
                        marginRight: 10,
                        color: '#8c8c8c'
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        color: '#333'
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#8c8c8c',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {item.type === MENTION_TYPES.CHARACTER
                        ? item.name
                        : item.description}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No matches found"
                />
              </div>
            )}
          </div>
        }
      >
        <div className="input-wrapper" style={{ position: 'relative' }}>
          <div className="backdrop-container" ref={backdropRef}>
            <div className="backdrop-content">{renderBackdropContent()}</div>
          </div>
          <Input.TextArea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onScroll={handleScroll}
            onKeyUp={handleCursorMove}
            onClick={handleCursorMove}
            onBlur={handleBlur}
            readOnly={disabled}
            autosize={{ minRows: rows, maxRows: rows }}
            placeholder={placeholder}
            className="rich-textarea"
          />
        </div>
      </Popover>
    </div>
  )
}

export default CommentInput
